import React, { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Assist, Play, Sparkles, Target, Trophy } from '@/components/icons/StatboxIcons'
import { SAMPLE_GAMES } from './matchPreviewSamples'
import { HintBubble, PreviewHintStyles } from './PreviewHint'

/**
 * Scaled copy of the hub game page (/g/:matchId): the score, then the Game Recap,
 * Video & Events and Players tabs. Laid out at phone width and scaled down.
 */

const BASE_WIDTH = 400
const TEAM = { A: '#3B82F6', B: '#EF4444' }
const INK = '#0B1220'
const MUTED = '#667085'
const LINE = '#E9ECF1'
const ease = [0.25, 1, 0.5, 1]

const RECAP_TABS = [
  ['recap', 'Game Recap'],
  ['video', 'Video & Events'],
  ['players', 'Players'],
]

/** columnsForSport in lib/recapColumns. */
const PLAYER_COLUMNS = {
  football: [['goals', 'G'], ['assists', 'A'], ['shots_on_target', 'SOT'], ['chances_created', 'CH'], ['successful_dribbles', 'DRB'], ['saves', 'SV']],
  basketball: [['points', 'PTS'], ['three_pts_made', '3PT'], ['assists', 'A'], ['rebounds', 'REB'], ['blocks', 'BLK'], ['turnover', 'TO']],
  padel: [['winner', 'WIN'], ['ace', 'ACE'], ['points', 'PTS'], ['smash', 'SMA'], ['unforced_errors', 'UE']],
}

/** HeadToHeadView stat rows. */
const H2H_ROWS = {
  football: [['shots_on_target', 'Shots on target'], ['shots_off_target', 'Shots off target'], ['assists', 'Assists'], ['chances_created', 'Chances created'], ['successful_dribbles', 'Dribbles'], ['successful_tib', 'Tackles & blocks'], ['saves', 'Saves']],
  basketball: [['fg_made', 'Field goals'], ['three_pts_made', '3-pointers'], ['assists', 'Assists'], ['rebounds', 'Rebounds'], ['blocks', 'Blocks'], ['turnover', 'Turnovers']],
  padel: [['points', 'Points'], ['winner', 'Winners'], ['ace', 'Aces'], ['smash', 'Smashes'], ['lob', 'Lobs'], ['unforced_errors', 'Unforced errors']],
}

const SAMPLE_KEY = { three_pts_made: 'three_made', free_throws_scored: 'ft_made', turnover: 'turnovers' }

/** Court colours behind the video stand-in. */
const SURFACE = {
  football: 'radial-gradient(120% 90% at 30% 15%, #2f7a50 0%, #1c5536 45%, #0d2a1b 100%)',
  basketball: 'radial-gradient(120% 90% at 30% 15%, #b9783f 0%, #8a5226 45%, #3a2210 100%)',
  padel: 'radial-gradient(120% 90% at 30% 15%, #2f6fb0 0%, #1f4c80 45%, #0e2240 100%)',
}

const num = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0)

function statOf(sport, key, s) {
  if (sport === 'football' && key === 'shots_on_target') return num(s.shots_on_target) + num(s.goals)
  if (sport === 'padel' && key === 'points') return num(s.serve) + num(s.return)
  if (key === 'rebounds') return num(s.offensive_rebound) + num(s.defensive_rebound)
  return num(s[SAMPLE_KEY[key] || key])
}

function teamTotal(game, side, key) {
  return game.players.filter((p) => p.side === side).reduce((acc, p) => acc + statOf(game.sport, key, p.s), 0)
}

/** The top performer cards CompletedGameRecap shows for hub games (no MVP grade). */
function topPerformers(game) {
  const { sport, players } = game
  const best = (valueOf) => {
    const top = [...players].sort((a, b) => valueOf(b) - valueOf(a))[0]
    return top && valueOf(top) > 0 ? top : null
  }
  const cards = []
  const push = (player, card) => {
    if (player) cards.push({ player, ...card })
  }
  if (sport === 'football') {
    const top = best((p) => num(p.s.goals))
    push(top, { key: 'goals', icon: Target, award: 'Top Scorer', value: top?.s.goals, label: 'goals' })
  }
  if (sport === 'basketball') {
    const pts = best((p) => num(p.s.points))
    push(pts, { key: 'pts', icon: Target, award: 'Top Scorer', value: pts?.s.points, label: 'points' })
    const reb = (p) => num(p.s.offensive_rebound) + num(p.s.defensive_rebound)
    const top = best(reb)
    push(top, { key: 'reb', icon: Trophy, award: 'Rebounds', value: top && reb(top), label: 'rebounds' })
  }
  if (sport === 'padel') {
    const win = best((p) => num(p.s.winner))
    push(win, { key: 'win', icon: Target, award: 'Winners', value: win?.s.winner, label: 'winners' })
    const ace = best((p) => num(p.s.ace))
    push(ace, { key: 'ace', icon: Sparkles, award: 'Aces', value: ace?.s.ace, label: 'aces' })
  }
  const playmaker = best((p) => num(p.s.assists))
  push(playmaker, { key: 'ast', icon: Assist, award: 'Playmaker', value: playmaker?.s.assists, label: 'assists' })
  return cards.slice(0, 3)
}

function fmtDateHeader(d) {
  return new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function fmtTime(t) {
  const [h, m] = t.substring(0, 5).split(':')
  const hr = parseInt(h, 10)
  return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`
}

/** Goal scorers in football, top points scorers elsewhere. */
function scorerLines(game, side) {
  if (game.sport === 'football') {
    const list = side === 'A' ? game.scorersHome : game.scorersAway
    return (list || []).map((item) => `${item.name} ${item.times}`)
  }
  const list = side === 'A' ? game.pointsHome : game.pointsAway
  return (list || []).slice(0, 2).map((item) => `${item.name} ${item.valueLine}`)
}

function Chrome() {
  return (
    <div className="flex h-8 shrink-0 items-center gap-2 bg-[#0A1220] px-3">
      <span className="flex gap-1" aria-hidden>
        <span className="h-2 w-2 rounded-full bg-white/15" />
        <span className="h-2 w-2 rounded-full bg-white/15" />
        <span className="h-2 w-2 rounded-full bg-white/15" />
      </span>
      <span className="flex-1 truncate rounded-full bg-white/[0.06] px-3 py-0.5 text-center text-[10px] text-white/50">
        statbox.co/g/thursday-night
      </span>
    </div>
  )
}

function Avatar({ name, side, size = 32 }) {
  const color = TEAM[side] || TEAM.A
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full font-bold"
      style={{ width: size, height: size, fontSize: size * 0.4, color, background: `${color}14`, boxShadow: `inset 0 0 0 1.5px ${color}40` }}
    >
      {name.charAt(0)}
    </span>
  )
}

function Scoreboard({ game, scoreA, scoreB }) {
  const sportName = game.sport.charAt(0).toUpperCase() + game.sport.slice(1)
  const side = (key, name, score, won) => (
    <div className={`flex min-w-0 flex-1 flex-col ${key === 'A' ? 'items-end text-right' : 'items-start text-left'}`}>
      <span className="flex max-w-full items-center gap-1.5">
        {key === 'A' ? <span className="h-3 w-1 shrink-0 rounded-full" style={{ background: TEAM.A }} /> : null}
        <span className="truncate text-[13px] font-semibold text-white/90">{name}</span>
        {key === 'B' ? <span className="h-3 w-1 shrink-0 rounded-full" style={{ background: TEAM.B }} /> : null}
      </span>
      <span className={`mt-1 text-[44px] font-extrabold leading-none tracking-[-0.04em] tabular-nums ${won ? 'text-white' : 'text-white/45'}`}>{score}</span>
      <div className="mt-2 space-y-0.5">
        {scorerLines(game, key).map((line) => (
          <p key={line} className="truncate text-[10.5px] text-white/50">{line}</p>
        ))}
      </div>
    </div>
  )
  return (
    <div className="relative overflow-hidden bg-[#0A1220] px-5 pb-4 pt-4">
      <span
        aria-hidden
        className="pointer-events-none absolute -top-24 left-1/2 h-48 w-[130%] -translate-x-1/2 rounded-full"
        style={{ background: 'radial-gradient(closest-side, rgba(113,205,217,0.18), transparent)' }}
      />
      <div className="relative flex items-center justify-between">
        <img src="/logo_white.png" alt="" className="h-3 w-auto opacity-90" />
        <span className="rounded-full bg-white/[0.08] px-2.5 py-1 text-[10px] font-semibold text-white/70">{game.community}</span>
      </div>
      <div className="relative mt-5 text-center">
        <p className="text-[12px] font-semibold text-white/85">{sportName} friendly</p>
        <p className="mt-0.5 text-[10.5px] text-white/45">
          {fmtDateHeader(game.datePlayed)}, {fmtTime(game.hourSlot)} at {game.venueName}
        </p>
      </div>
      <div className="relative mt-4 flex items-start gap-5">
        {side('A', game.home, scoreA, scoreA > scoreB)}
        <span className="mt-7 shrink-0 rounded-full bg-white/[0.08] px-2 py-0.5 text-[9.5px] font-bold text-white/70">Full time</span>
        {side('B', game.away, scoreB, scoreB > scoreA)}
      </div>
    </div>
  )
}

function Tabs({ tab, onChange, interactive, showHint }) {
  return (
    <div className="relative bg-[#0A1220] px-4 pb-3">
      <div className="relative grid grid-cols-3 rounded-full bg-white/[0.07] p-1">
        {RECAP_TABS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            tabIndex={interactive ? 0 : -1}
            className={`relative z-10 whitespace-nowrap rounded-full py-2 text-[11.5px] font-semibold transition-colors ${tab === id ? 'text-[#0A1220]' : 'text-white/60 hover:text-white/85'}`}
          >
            {tab === id ? (
              <motion.span
                layoutId="hub-preview-tab"
                className="absolute inset-0 -z-10 rounded-full bg-white"
                transition={{ duration: 0.28, ease }}
              />
            ) : null}
            {label}
          </button>
        ))}
      </div>
      {showHint ? (
        <HintBubble fontSize={10} style={{ right: 26, top: 30, zIndex: 20 }}>
          Tap
        </HintBubble>
      ) : null}
    </div>
  )
}

function Card({ title, aside, children, className = '' }) {
  return (
    <section className={`rounded-[20px] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-16px_rgba(16,24,40,0.18)] ring-1 ring-[#EEF0F4] ${className}`}>
      {title ? (
        <div className="mb-3 flex items-center justify-between gap-2">
          <p className="text-[13px] font-bold tracking-[-0.01em]" style={{ color: INK }}>{title}</p>
          {aside}
        </div>
      ) : null}
      {children}
    </section>
  )
}

function VideoStill({ game, progress = 0.18, label }) {
  return (
    <div className="relative overflow-hidden rounded-2xl" style={{ paddingBottom: '56.25%', background: SURFACE[game.sport] }}>
      <span aria-hidden className="absolute inset-[9%] rounded-[3px] border border-white/15" />
      <span aria-hidden className="absolute left-1/2 top-[9%] h-[82%] w-px bg-white/15" />
      <span aria-hidden className="absolute left-1/2 top-1/2 h-14 w-14 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/15" />
      <span aria-hidden className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_45%,rgba(0,0,0,0.65)_100%)]" />
      <span className="absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 ring-1 ring-white/30 backdrop-blur-md">
        <span className="ml-1 h-0 w-0 border-y-[7px] border-l-[11px] border-y-transparent border-l-white" />
      </span>
      <span className="absolute right-2.5 top-2.5 rounded-full bg-black/45 px-2 py-0.5 text-[9.5px] font-semibold tabular-nums text-white backdrop-blur">
        {game.reel}
      </span>
      <div className="absolute inset-x-3 bottom-2.5">
        {label ? <p className="mb-1.5 truncate text-[11px] font-semibold text-white">{label}</p> : null}
        <span className="relative block h-[3px] rounded-full bg-white/25">
          <motion.span
            className="absolute inset-y-0 left-0 rounded-full bg-[#71cdd9]"
            animate={{ width: `${Math.round(progress * 100)}%` }}
            transition={{ duration: 0.35, ease }}
          />
        </span>
      </div>
    </div>
  )
}

function TeamStats({ game }) {
  return (
    <Card
      title="Team stats"
      aside={
        <span className="flex items-center gap-2 text-[10px] font-semibold" style={{ color: MUTED }}>
          <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full" style={{ background: TEAM.A }} />{game.home}</span>
          <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full" style={{ background: TEAM.B }} />{game.away}</span>
        </span>
      }
    >
      <div className="space-y-3">
        {H2H_ROWS[game.sport].map(([key, label]) => {
          const a = teamTotal(game, 'A', key)
          const b = teamTotal(game, 'B', key)
          const max = Math.max(a, b, 1)
          return (
            <div key={key}>
              <div className="flex items-baseline justify-between text-[12px]">
                <span className={`w-8 tabular-nums ${a > b ? 'font-bold' : 'font-medium'}`} style={{ color: a > b ? TEAM.A : MUTED }}>{a}</span>
                <span className="min-w-0 flex-1 truncate text-center text-[11px] font-medium" style={{ color: '#344054' }}>{label}</span>
                <span className={`w-8 text-right tabular-nums ${b > a ? 'font-bold' : 'font-medium'}`} style={{ color: b > a ? TEAM.B : MUTED }}>{b}</span>
              </div>
              <div className="mt-1.5 flex gap-1">
                <span className="flex h-[5px] flex-1 justify-end overflow-hidden rounded-full bg-[#F1F3F6]">
                  <span className="h-full rounded-full" style={{ width: `${(a / max) * 100}%`, background: TEAM.A, opacity: a >= b ? 1 : 0.35 }} />
                </span>
                <span className="flex h-[5px] flex-1 overflow-hidden rounded-full bg-[#F1F3F6]">
                  <span className="h-full rounded-full" style={{ width: `${(b / max) * 100}%`, background: TEAM.B, opacity: b >= a ? 1 : 0.35 }} />
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </Card>
  )
}

function TopPerformers({ game }) {
  const cards = useMemo(() => topPerformers(game), [game])
  return (
    <Card title="Top performers" aside={<Trophy className="h-4 w-4 text-[#D4A017]" />}>
      <div className="-mx-1 divide-y" style={{ borderColor: LINE }}>
        {cards.map((card) => {
          const Icon = card.icon
          return (
            <div key={card.key} className="flex items-center gap-3 px-1 py-2.5 first:pt-0 last:pb-0" style={{ borderColor: LINE }}>
              <Avatar name={card.player.name} side={card.player.side} size={36} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-bold" style={{ color: INK }}>{card.player.name}</p>
                <p className="mt-0.5 flex items-center gap-1 text-[10.5px] font-medium" style={{ color: MUTED }}>
                  <Icon className="h-3 w-3" />
                  {card.award}
                </p>
              </div>
              <p className="text-right">
                <span className="block text-[22px] font-extrabold leading-none tracking-[-0.03em] tabular-nums" style={{ color: INK }}>{card.value}</span>
                <span className="text-[10px] font-medium" style={{ color: MUTED }}>{card.label}</span>
              </p>
            </div>
          )
        })}
      </div>
    </Card>
  )
}

function RecapTab({ game }) {
  return (
    <>
      <Card title="Game highlights" aside={<span className="text-[10.5px] font-medium" style={{ color: MUTED }}>{game.events.length} moments</span>}>
        <VideoStill game={game} />
      </Card>
      <TopPerformers game={game} />
      <TeamStats game={game} />
    </>
  )
}

function VideoTab({ game, interactive }) {
  const [active, setActive] = useState(0)
  const event = game.events[active]
  return (
    <>
      <Card>
        <VideoStill game={game} progress={(active + 1) / (game.events.length + 1)} label={`${event[1]} by ${event[3]}`} />
      </Card>
      <Card title="Key moments" aside={<span className="text-[10.5px] font-medium" style={{ color: MUTED }}>Tap to watch</span>}>
        <ol className="-mx-2">
          {game.events.map(([minute, type, color, name, side], index) => (
            <li key={`${minute}-${index}`}>
              <button
                type="button"
                onClick={() => setActive(index)}
                tabIndex={interactive ? 0 : -1}
                className={`flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors ${index === active ? 'bg-[#F2F7F8]' : 'hover:bg-[#F7F8FA]'}`}
              >
                <span className="w-7 shrink-0 text-[11px] font-semibold tabular-nums" style={{ color: MUTED }}>{minute}</span>
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: color, boxShadow: `0 0 0 3px ${color}22` }} />
                <span className="min-w-0 flex-1 truncate text-[12.5px]" style={{ color: INK }}>
                  <span className="font-semibold">{type}</span>
                  <span style={{ color: MUTED }}> {name}</span>
                </span>
                <span className="h-3 w-1 shrink-0 rounded-full" style={{ background: TEAM[side] }} />
              </button>
            </li>
          ))}
        </ol>
      </Card>
    </>
  )
}

function PlayersTab({ game }) {
  const columns = PLAYER_COLUMNS[game.sport]
  const [first] = columns
  const rows = [...game.players].sort((a, b) => statOf(game.sport, first[0], b.s) - statOf(game.sport, first[0], a.s))
  const best = Object.fromEntries(columns.map(([key]) => [key, Math.max(...game.players.map((p) => statOf(game.sport, key, p.s)))]))
  return (
    <Card title="Player stats" aside={<span className="text-[10.5px] font-medium" style={{ color: MUTED }}>{game.players.length} players</span>}>
      <table className="w-full text-[12px]">
        <thead>
          <tr>
            <th className="pb-2 text-left text-[10px] font-semibold" style={{ color: MUTED }}>Player</th>
            {columns.map(([key, short]) => (
              <th key={key} className="w-9 pb-2 text-center text-[10px] font-semibold" style={{ color: MUTED }}>{short}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((player) => (
            <tr key={player.name} className="border-t" style={{ borderColor: LINE }}>
              <td className="py-2 pr-1">
                <span className="flex items-center gap-2">
                  <Avatar name={player.name} side={player.side} size={26} />
                  <span className="min-w-0">
                    <span className="block truncate font-semibold" style={{ color: INK }}>{player.name}</span>
                    <span className="flex items-center gap-0.5 text-[9.5px] font-semibold text-[#0E7C8A]">
                      <Play className="h-2.5 w-2.5 fill-current" strokeWidth={0} />
                      Highlights
                    </span>
                  </span>
                </span>
              </td>
              {columns.map(([key]) => {
                const value = statOf(game.sport, key, player.s)
                const top = value > 0 && value === best[key]
                return (
                  <td key={key} className="py-2 text-center tabular-nums">
                    <span
                      className={`inline-flex min-w-[24px] justify-center rounded-md px-1 py-0.5 ${top ? 'font-bold' : 'font-medium'}`}
                      style={top ? { background: '#E8F6F8', color: '#0B5C66' } : { color: value ? '#344054' : '#B4BAC5' }}
                    >
                      {value}
                    </span>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  )
}

/** Interactive mock of the game page people land on after uploading. */
export function HubPreview({ sport, width, height, interactive = true }) {
  const game = SAMPLE_GAMES[sport] || SAMPLE_GAMES.Football
  const [tab, setTab] = useState('recap')
  const [explored, setExplored] = useState(false)
  const reduceMotion = useReducedMotion()
  const scale = width / BASE_WIDTH
  const innerHeight = Math.round(height / scale)
  const [scoreA, scoreB] = game.score.split('-').map((value) => Number(value.trim()))

  useEffect(() => {
    setTab('recap')
  }, [sport])

  const changeTab = (id) => {
    setTab(id)
    setExplored(true)
  }

  return (
    <div
      className="overflow-hidden rounded-[18px] bg-[#0A1220] shadow-[0_24px_60px_-28px_rgba(7,16,24,0.55)] ring-1 ring-black/5"
      style={{ width, height }}
      aria-hidden={interactive ? undefined : true}
    >
      <PreviewHintStyles />
      <div
        className={`relative flex origin-top-left flex-col bg-[#F4F6F9] font-jakarta ${interactive ? '' : 'pointer-events-none'}`}
        style={{ width: BASE_WIDTH, height: innerHeight, transform: `scale(${scale})`, color: INK }}
      >
        <Chrome />
        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: 'none' }}
        >
          <div key={sport}>
            <Scoreboard game={game} scoreA={scoreA} scoreB={scoreB} />
            <div className="sticky top-0 z-10">
              <Tabs tab={tab} onChange={changeTab} interactive={interactive} showHint={interactive && !explored} />
            </div>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={tab}
                className="space-y-3 px-3.5 pb-8 pt-3.5"
                initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18, ease }}
              >
                {tab === 'recap' ? <RecapTab game={game} /> : null}
                {tab === 'video' ? <VideoTab game={game} interactive={interactive} /> : null}
                {tab === 'players' ? <PlayersTab game={game} /> : null}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  )
}
